/*
 * <license header>
 */

import "core-js/stable";
import "regenerator-runtime/runtime";
import React from "react";
import { createRoot } from "react-dom/client";

import Runtime, { init } from "@adobe/exc-app";

import App from "./components/App";
import launchScriptUrls from "./components/analytics/script-config";
import { ENV } from "./env";
import "./index.css";
import actionWebInvoke from "./utils/actionWebInvoke";
import actions from "./config.json";
import { getAdobeDataLayer } from "./utils/adobeDataLayerUtils";
import { syncBeverageUserSessionForAcid } from "./utils/beverageSession";

window.React = require("react");
// Initialize before the async Adobe Launch script is appended.
getAdobeDataLayer();
initAdobeLaunchScript();

/* Here you can bootstrap your application and configure the integration with the Adobe Experience Cloud Shell */
try {
  // attempt to load the Experience Cloud Runtime
  require("./exc-runtime");
  // if there are no errors, bootstrap the app in the Experience Cloud Shell
  init(bootstrapInExcShell);
} catch (e) {
  console.log("application not running in Adobe Experience Cloud Shell");
  // fallback mode, run the application without the Experience Cloud Runtime
  bootstrapRaw();
}

function bootstrapRaw() {
  /* **here you can mock the exc runtime and ims objects** */

  // Check for feature flagged redirect only for /offers path
  if (ENV.FEATURE_FLAGS.redirect && window.location.hash.includes("/offers")) {
    window.location.href =
      "https://www.marriott.com/offers/resort-vacations-1355738";
    return;
  }

  const mockRuntime = { on: () => { } };
  const mockIms = {};

  // We need to parse query params even if they are appended after the hash
  // e.g. https://dev-passions.marriott.com/#/beverage/preferences?acid=...&site=...&sessionid=...
  let searchString = window.location.search;

  // If no search string is found, check if query params are appended to the hash fragment
  if (!searchString && window.location.hash.includes("?")) {
    searchString = window.location.hash.substring(window.location.hash.indexOf("?"));
  }

  const urlParams = new URLSearchParams(searchString);
  const sessionid = urlParams.get("sessionid");
  const site = urlParams.get("site");
  const xRequestId = urlParams.get("x-request-id");
  const returnPath = urlParams.get("returnPath");

  const renderApp = () => {
    // render the actual react application and pass along the runtime object to make it available to the App
    const container = document.getElementById("root");
    const root = createRoot(container);
    root.render(<App runtime={mockRuntime} ims={mockIms} />);
  };

  if (sessionid && site && xRequestId) {
    console.log("Initiating seamless auth flow");
    // We fall back to standard auth flow rendering if action configuration is not yet available in config.json
    if (!actions["marriott-passions-v3/seamless-auth"]) {
      console.error("seamless-auth action not configured.");
      renderApp();
      return;
    }

    actionWebInvoke(
      actions["marriott-passions-v3/seamless-auth"],
      {},
      {
        sessionid,
        site,
        "x-request-id": xRequestId,
      }
    )
      .then((response) => {
        if (response.access_token) {
          sessionStorage.setItem("access_token", response.access_token);
          if (response.id_token) {
            sessionStorage.setItem("id_token", response.id_token);
          }
          if (response.acid) {
            syncBeverageUserSessionForAcid(response.acid);
            sessionStorage.setItem("acid", response.acid);
          }

          // Remove query params from URL so we don't try to auth again on refresh
          const currentUrl = new URL(window.location.href);

          // Clean up url parameters and navigate
          // Preserve the full hash path including query params if they exist
          let defaultPath = "/campaign?expId=beverage";
          if (currentUrl.hash && currentUrl.hash.length > 1 && currentUrl.hash !== "#/") {
            // Keep the full hash path including all query params
            defaultPath = currentUrl.hash.replace("#", "");
          }

          let newPath = returnPath || defaultPath;
          if (!newPath.startsWith('/')) {
            newPath = `/${newPath}`;
          }

          // Remove auth-related query params from the path
          const [pathPart, queryPart] = newPath.split('?');
          let cleanPath = pathPart;
          if (queryPart) {
            const queryParams = new URLSearchParams(queryPart);
            // Remove auth params
            queryParams.delete('sessionid');
            queryParams.delete('site');
            queryParams.delete('x-request-id');
            queryParams.delete('returnPath');
            const remainingParams = queryParams.toString();
            if (remainingParams) {
              cleanPath = `${pathPart}?${remainingParams}`;
            }
          }

          // Use hash based routing if that's what the app uses
          // Replace window location to force the router to pick it up properly
          const finalUrl = new URL(window.location.href);
          finalUrl.search = '';
          finalUrl.hash = cleanPath;

          window.location.replace(finalUrl.toString());
        } else {
          console.error("Seamless auth failed: No access token in response");
        }
      })
      .catch((error) => {
        console.error("Seamless auth request failed:", error);
      })
      .finally(() => {
        renderApp();
      });
  } else {
    renderApp();
  }
}

function bootstrapInExcShell() {
  // get the Experience Cloud Runtime object
  const runtime = Runtime();

  // use this to set a favicon
  // runtime.favicon = 'url-to-favicon'

  // use this to respond to clicks on the app-bar title
  // runtime.heroClick = () => window.alert('Did I ever tell you you\'re my hero?')

  // ready event brings in authentication/user info
  runtime.on("ready", ({ imsOrg, imsToken, imsProfile }) => {
    // tell the exc-runtime object we are done
    runtime.done();
    console.log("Ready! received imsProfile:", imsProfile);

    // Check for feature flagged redirect only for /offers path
    if (
      ENV.FEATURE_FLAGS.redirect &&
      window.location.hash.includes("/offers")
    ) {
      window.location.href =
        "https://www.marriott.com/offers/resort-vacations-1355738";
      return;
    }

    const ims = {
      profile: imsProfile,
      org: imsOrg,
      token: imsToken,
    };
    // render the actual react application and pass along the runtime and ims objects to make it available to the App
    const container = document.getElementById("root");
    const root = createRoot(container);
    root.render(<App runtime={runtime} ims={ims} />);
    // ReactDOM.render(
    //   <App runtime={runtime} ims={ims} />,
    //   document.getElementById('root')
    // )
  });

  // set solution info, shortTitle is used when window is too small to display full title
  runtime.solution = {
    icon: "AdobeExperienceCloud",
    title: "marriottpassionsv3",
    shortTitle: "JGR",
  };
  runtime.title = "marriottpassionsv3";
}

function initAdobeLaunchScript() {
  const adobeLaunchScript = document.createElement("script");
  adobeLaunchScript.src =
    process.env.ANALYTICS_PROD_MODE === "true"
      ? launchScriptUrls.prod
      : launchScriptUrls.nonProd;
  adobeLaunchScript.async = true;
  document.head.appendChild(adobeLaunchScript);
}
